import type { ObraPendencia, ObraStatus } from '@obras/shared';

/**
 * O recorte que outra tela manda para a lista de obras, pela URL.
 *
 * É como o painel abre a lista já filtrada: o contador de contratos vencidos
 * leva a `/obras?pendencias=contrato_vencido`. Cada campo é uma lista em CSV,
 * o mesmo formato que a API aceita — a URL fica legível e o valor passa
 * adiante sem tradução.
 *
 * A lista aplica o recorte, grava-o como o seu estado e limpa a URL: o que
 * fica valendo é o estado guardado, igual ao de qualquer outro filtro.
 */
export interface ObrasSearch {
  readonly pendencias?: string;
  readonly statuses?: string;
  readonly fiscalIds?: string;
}

const KEYS = ['pendencias', 'statuses', 'fiscalIds'] as const;

/** Guarda só o que é texto não vazio; o resto da URL é ignorado, não erro. */
export function validateObrasSearch(raw: Record<string, unknown>): ObrasSearch {
  const search: { -readonly [K in keyof ObrasSearch]: string } = {};

  for (const key of KEYS) {
    const value = raw[key];
    if (typeof value === 'string' && value !== '') search[key] = value;
  }

  return search;
}

/** Monta o recorte a partir das listas, deixando de fora as vazias. */
export function obrasSearch(recorte: {
  readonly pendencias?: readonly ObraPendencia[];
  readonly statuses?: readonly ObraStatus[];
  readonly fiscalIds?: readonly string[];
}): ObrasSearch {
  const search: { -readonly [K in keyof ObrasSearch]: string } = {};

  for (const key of KEYS) {
    const values = recorte[key];
    if (values && values.length > 0) search[key] = values.join(',');
  }

  return search;
}

export function hasObrasSearch(search: ObrasSearch): boolean {
  return KEYS.some((key) => search[key] !== undefined);
}

/** Um atalho para a lista de obras com o recorte já aplicado. */
export interface ObrasLink {
  readonly to: '/obras';
  readonly search?: ObrasSearch | undefined;
}
